import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  buildOtherExpenseCategoryOptions,
  getOtherExpenseCategories,
  getOtherExpenseMargins,
  OtherExpenseCategory,
  OtherExpenseCategoryOption,
  OtherExpensesMargin,
  setOtherExpenseCategoryArchived,
} from 'api/otherExpensesApi';
import { blueGrey } from '@mui/material/colors';
import AddOrEditOtherExpenseRateDialog from './AddOrEditOtherExpenseMarginDialog';
import AddOrEditCustomCategoryDialog from './AddOrEditCustomCategoryDialog';
import AddIcon from '@mui/icons-material/Add';
import { OtherExpensesMarginTableComponent } from './OtherExpensesMarginTableComponent';
import OtherExpensesMarginDetailsView from './OtherExpensesMarginDetailsView';

export default function OtherExpensesMarginCardHomePage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    OtherExpenseCategoryOption | undefined
  >(undefined);
  const [editItem, setEditItem] = useState<OtherExpensesMargin | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState<{
    open: boolean;
    editCategory: OtherExpenseCategory | null;
  }>({ open: false, editCategory: null });
  const [categoryToArchive, setCategoryToArchive] =
    useState<OtherExpenseCategory | null>(null);

  const { data: otherExpensesData, isFetching } = useQuery(
    ['other-expenses-margin'],
    getOtherExpenseMargins
  );
  const { data: customCategories, isFetching: isFetchingCategories } =
    useQuery(['other-expense-categories', { includeArchived: true }], () =>
      getOtherExpenseCategories({ includeArchived: true })
    );

  const tableCategories = buildOtherExpenseCategoryOptions(
    (customCategories || []).filter((c) => showArchived || !c.isArchived)
  );
  const hasArchivedCategories = (customCategories || []).some(
    (c) => c.isArchived
  );

  const archiveMutation = useMutation(setOtherExpenseCategoryArchived, {
    onSuccess: (category) => {
      queryClient.invalidateQueries(['other-expense-categories']);
      enqueueSnackbar(
        category.isArchived
          ? `"${category.name}" archived`
          : `"${category.name}" restored`,
        { variant: 'success' }
      );
      setCategoryToArchive(null);
    },
    onError: () => {
      enqueueSnackbar('Failed to update the category', { variant: 'error' });
    },
  });

  const handleCategoryClick = (category: OtherExpenseCategoryOption) => {
    setSelectedCategory(category);
    setOpenDetails(true);
  };

  const handleToggleArchive = (category: OtherExpenseCategory) => {
    if (category.isArchived) {
      // Restoring hides nothing, so it needs no confirmation.
      archiveMutation.mutate({ id: category.id, isArchived: false });
    } else {
      setCategoryToArchive(category);
    }
  };

  const handleOpenDialog = (item: OtherExpensesMargin | null = null) => {
    setEditItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditItem(null);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedCategory(undefined);
  };

  const handleEditFromDetails = (rate: OtherExpensesMargin) => {
    setEditItem(rate);
    setOpenDialog(true);
  };

  return (
    <Box sx={{ m: 3, backgroundColor: '#fff', borderRadius: 1 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          p: '1rem',
        }}>
        <Typography variant="h6" sx={{ color: blueGrey[700] }}>
          Margins by Category
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="large"
            disableElevation
            onClick={() =>
              setCategoryDialog({ open: true, editCategory: null })
            }
            startIcon={<AddIcon />}>
            Add Custom Category
          </Button>
          <Button
            variant="contained"
            size="large"
            disableElevation
            onClick={() => handleOpenDialog()}
            sx={{
              px: '2rem',
            }}
            startIcon={<AddIcon />}>
            Add New
          </Button>
        </Stack>
      </Stack>
      {hasArchivedCategories && (
        <Box sx={{ px: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label="Show archived categories"
          />
        </Box>
      )}
      <Box sx={{ px: 2, pb: 2 }}>
        {isFetching || isFetchingCategories ? (
          <LinearProgress />
        ) : (
          <OtherExpensesMarginTableComponent
            otherExpensesMargins={otherExpensesData || []}
            categories={tableCategories}
            onCategoryClick={handleCategoryClick}
            onRenameCategory={(category) =>
              setCategoryDialog({ open: true, editCategory: category })
            }
            onToggleArchiveCategory={handleToggleArchive}
          />
        )}
      </Box>
      <AddOrEditOtherExpenseRateDialog
        openDialog={openDialog}
        onClose={handleCloseDialog}
        editItem={editItem}
        onClearEdit={() => setEditItem(null)}
      />
      <OtherExpensesMarginDetailsView
        open={openDetails}
        handleClose={handleCloseDetails}
        selectedCategory={selectedCategory}
        onEdit={handleEditFromDetails}
      />
      <AddOrEditCustomCategoryDialog
        open={categoryDialog.open}
        editCategory={categoryDialog.editCategory}
        onClose={() => setCategoryDialog({ open: false, editCategory: null })}
      />
      <Dialog
        open={Boolean(categoryToArchive)}
        onClose={() => setCategoryToArchive(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Archive "{categoryToArchive?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It won't be offered when adding new expenses. Expenses already
            recorded under it keep it, and its margins still apply to them.
            You can restore it at any time from "Show archived categories".
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ mx: 2, my: 1 }}>
          <Button
            onClick={() => setCategoryToArchive(null)}
            disabled={archiveMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={archiveMutation.isLoading}
            onClick={() =>
              categoryToArchive &&
              archiveMutation.mutate({
                id: categoryToArchive.id,
                isArchived: true,
              })
            }>
            {archiveMutation.isLoading ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
